import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Chip,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Menu,
    MenuItem
} from '@mui/material';
import {
    ContentCopy as CopyIcon,
    Download as DownloadIcon,
    Delete as DeleteIcon,
    MoreVert as MoreVertIcon,
    Note as NoteIcon,
    Visibility as ViewIcon
} from '@mui/icons-material';
import { Character, SimCReport } from '../../types/simc';
import {useSimCReports} from "../../hooks/useReports";

interface ReportViewerProps {
    character: Character | null;
    onDeleteReport: (reportId: string) => void;
}

const formatDate = (date: Date): string => {
    return `${date.getFullYear()}_${date.getMonth()}_${date.getDay()}_${date.getHours()}_${date.getMinutes()}_${date.getSeconds()}`
}

export const ReportViewer: React.FC<ReportViewerProps> = ({
                                                              character,
                                                              onDeleteReport
                                                          }) => {
    const {
        updateNote
    } = useSimCReports();

    const [selectedReport, setSelectedReport] = useState<SimCReport | null>(null);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [noteDialogOpen, setNoteDialogOpen] = useState(false);
    const [noteText, setNoteText] = useState('');
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [menuReportId, setMenuReportId] = useState<string>('');

    const handleCopy = async (report: SimCReport) => {
        try {
            await navigator.clipboard.writeText(report.report);
            // You could add a toast notification here
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
        }
    };

    const handleDownload = (report: SimCReport) => {
        const blob = new Blob([report.report], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${report.character}_${formatDate(report.uploadedAt)}.simc`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleViewReport = (report: SimCReport) => {
        setSelectedReport(report);
        setViewDialogOpen(true);
    };

    const handleAddNote = (report: SimCReport) => {
        setSelectedReport(report);
        setNoteText(report.note || '');
        setNoteDialogOpen(true);
        setMenuAnchor(null);
    };

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>, reportId: string) => {
        setMenuAnchor(event.currentTarget);
        setMenuReportId(reportId);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
        setMenuReportId('');
    };

    const formatFileSize = (bytes: number) => {
        return (bytes / 1024).toFixed(1) + 'KB';
    };

    if (!character) {
        return (
            <Paper
                elevation={2}
                sx={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 2
                }}
            >
                <Typography variant="body1" color="text.secondary">
                    Select a character to view their reports
                </Typography>
            </Paper>
        );
    }

    return (
        <>
            <Paper elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 2 }}>
                {/* Header */}
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="h6">
                        {character.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">

                    </Typography>
                    {character.reports.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Chip label={character.reports[0].class} size="small" color="primary" />
                            <Chip label={character.reports[0].spec} size="small" variant="outlined" />
                        </Box>
                    )}
                </Box>

                {/* Reports List */}
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                    {character.reports.length === 0 ? (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                No reports for this character yet
                            </Typography>
                        </Box>
                    ) : (
                        <List>
                            {character.reports.map((report, index) => (
                                <ListItem
                                    key={report.id}
                                    divider={index < character.reports.length - 1}
                                    sx={{
                                        alignItems: 'flex-start',
                                        '&:hover': {
                                            backgroundColor: 'action.hover'
                                        }
                                    }}
                                    secondaryAction={
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <Tooltip title="View report">
                                                <IconButton size="small" onClick={() => handleViewReport(report)}>
                                                    <ViewIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Copy to clipboard">
                                                <IconButton size="small" onClick={() => handleCopy(report)}>
                                                    <CopyIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Download">
                                                <IconButton size="small" onClick={() => handleDownload(report)}>
                                                    <DownloadIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>

                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleMenuClick(e, report.id)}
                                            >
                                                <MoreVertIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    }
                                >
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="subtitle2">
                                                    Report #{character.reports.length - index}
                                                </Typography>
                                                {report.note && (
                                                    <Tooltip title={report.note}>
                                                        <NoteIcon fontSize="small" color="action" />
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        }
                                        secondary={
                                            <Box>
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                    {formatDate(report.uploadedAt!)}
                                                </Typography>
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                    Size: {formatFileSize(report.size)}
                                                </Typography>
                                                {report.note && (
                                                    <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                                        "{report.note}"
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Box>
            </Paper>

            {/* Action Menu */}
            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={() => {
                    const report = character.reports.find(r => r.id === menuReportId);
                    if (report) handleAddNote(report);
                }}>
                    <NoteIcon fontSize="small" sx={{ mr: 1 }} />
                    {character.reports.find(r => r.id === menuReportId)?.note ? 'Edit Note' : 'Add Note'}
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        onDeleteReport(menuReportId);
                        handleMenuClose();
                    }}
                    sx={{ color: 'error.main' }}
                >
                    <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                    Delete Report
                </MenuItem>
            </Menu>

            {/* View Report Dialog */}
            <Dialog
                open={viewDialogOpen}
                onClose={() => setViewDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {selectedReport?.spec} {selectedReport?.class}
                </DialogTitle>
                <DialogContent>
                    <Typography
                        component="pre"
                        sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.875rem',
                            lineHeight: 1.4,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                            p: 2,
                            borderRadius: 1,
                            border: 1,
                            borderColor: 'divider',
                            maxHeight: 400,
                            overflow: 'auto'
                        }}
                    >
                        {selectedReport?.report}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
                    {selectedReport && (
                        <>
                            <Button onClick={() => handleCopy(selectedReport)}>Copy</Button>
                            <Button onClick={() => handleDownload(selectedReport)}>Download</Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* Note Dialog */}
            <Dialog open={noteDialogOpen} onClose={() => setNoteDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {selectedReport?.note ? 'Edit Note' : 'Add Note'}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Note"
                        fullWidth
                        multiline
                        rows={3}
                        variant="outlined"
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        placeholder="Add a note about this report..."
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNoteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={() => {
                        if (selectedReport) {
                            updateNote({
                                ...selectedReport,
                                note: noteText,
                            });
                        }
                        setNoteDialogOpen(false);
                    }}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

